import { useEffect, useMemo, useState } from "react";
import { useSecrets } from "@/context/SecretsContext";

/** Site-wide 6.13 second time loss. Respects prefers-reduced-motion. */
export const NullSixBlackout = () => {
  const { glitchPhase } = useSecrets();
  const [remaining, setRemaining] = useState(6.13);

  useEffect(() => {
    if (glitchPhase !== "blackout") {
      setRemaining(6.13);
      return undefined;
    }
    const started = Date.now();
    const id = window.setInterval(() => {
      const left = 6.13 - (Date.now() - started) / 1000;
      setRemaining(left > 0 ? left : 0);
    }, 50);
    return () => window.clearInterval(id);
  }, [glitchPhase]);

  if (glitchPhase !== "blackout") return null;

  return (
    <div
      className="fixed inset-0 z-[95] flex flex-col items-center justify-center bg-[hsl(220_22%_3%)] px-6 text-center"
      data-testid="null-six-blackout"
      role="status"
      aria-live="assertive"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[hsl(2_80%_60%)]">
        Mesh integrity violation
      </p>
      <p className="mt-6 font-mono text-5xl tabular-nums text-[hsl(32_92%_62%)] sm:text-7xl">
        {remaining.toFixed(2)}
      </p>
      <p className="mt-6 max-w-sm font-mono text-[11px] uppercase leading-relaxed tracking-[0.16em] text-[hsl(215_12%_58%)]">
        Six point one three seconds are being removed from this session.
        <span className="mt-2 block text-[hsl(215_10%_42%)]">
          You will not remember them. Nothing is wrong.
        </span>
      </p>
      <div className="fx-scanlines pointer-events-none absolute inset-0 opacity-[0.12]" />
    </div>
  );
};

/** Rows of small light markers, the way the rescue crews left them. */
export const MemorialLights = () => {
  const { memorial } = useSecrets();

  const lights = useMemo(
    () =>
      Array.from({ length: 150 }, (_, i) => ({
        id: i,
        left: Math.random() * 98 + 1,
        top: Math.random() * 92 + 4,
        delay: Math.random() * 3.4,
        size: Math.random() * 3 + 2.5
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [memorial]
  );

  if (!memorial) return null;

  return (
    <div
      className="pointer-events-none fixed inset-0 z-[80] overflow-hidden"
      data-testid="memorial-lights"
      aria-hidden="true"
    >
      {lights.map((l) => (
        <span
          key={l.id}
          className="memorial-light"
          style={{
            left: `${l.left}%`,
            top: `${l.top}%`,
            width: `${l.size}px`,
            height: `${l.size}px`,
            animationDelay: `${l.delay}s`
          }}
        />
      ))}
      <div className="absolute inset-x-0 bottom-10 flex justify-center px-6">
        <div className="panel max-w-md px-5 py-4 text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(32_80%_66%)]">
            Arrival concourse · east wall
          </p>
          <p className="mt-2 text-sm leading-relaxed text-[hsl(210_14%_84%)]">
            Someone started placing a small light for each of the dead. Nobody
            asked who. By the third day there were two thousand one hundred and
            eighty-four.
          </p>
        </div>
      </div>
    </div>
  );
};
